import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { deleteUser, deleteUserFully, patchRevokeSuperAdmin } from '../../../../api/usersApi';
import { ContainedButton, OutlinedButton } from '../../Buttons';
import styles from './DeleteUserSuperAdminModal.module.scss';
import { FOLDER_TYPE, ROLES, RolesLowerCase, USER } from '../../../../enums';
import { displayRole } from '../../../../helpers';
import Modal from '../../Modal';
import { useHotkeys } from 'react-hotkeys-hook';
import DeleteUserModal from '../DeleteUserModal/DeleteUserModal';
import { useAppDispatch } from '../../../../store/hooks';
import { updateNotificationTrigger } from '../../../../store/sharedStore/sharedStoreSlice';
import { getFoldersByUserIdAll } from '../../../../api';

type TDeleteUserModalProps = {
  isOpen: boolean;
  switchModal: () => void;
  user: USER;
  fetchData: () => void;
};

const DeleteUserSuperAdminModal: React.FC<TDeleteUserModalProps> = ({ isOpen, switchModal, user, fetchData }) => {
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const onClose = () => {
    switchModal();
    setLoading(false);
    setConfirmDeletion(false);
    setShowDeleteFromOrgs(false);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await patchRevokeSuperAdmin(user.id);
      await fetchData();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        console.log(err);
      }
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const switchConfirmDeleteModal = () => {
    setConfirmDeletion(!confirmDeletion);
  };

  const proceedToConfirmDeleteModal = () => {
    if (user?.role?.title === RolesLowerCase.SUPER_ADMIN && user?.organization_users?.length === 0) {
      switchFinalConfirm();
    } else {
      switchConfirmDeleteModal();
    }
  };

  const [finalConfirm, setFinalConfirm] = useState(false);
  const switchFinalConfirm = () => {
    setFinalConfirm(true);
  };
  const [finalFolders, setFinalFolders] = useState<FOLDER_TYPE[]>([]);
  const fetchAllUserFolders = async () => {
    try {
      setLoading(true);

      const res = await getFoldersByUserIdAll(user.id);
      if (res.data.documents.length) {
        setFinalFolders(res.data.documents);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const submitDeletion = async () => {
    if (user.role.title === RolesLowerCase.SUPER_ADMIN && user.organization_users?.length === 0) {
      submit();
      return;
    }
    setLoading(true);
    try {
      let orgId = user?.organization?.id;
      if (user?.organization_users?.length && user.organization_users[0]?.organization?.id) {
        orgId = user.organization_users[0]?.organization?.id;
      }
      await deleteUserFully(user.id, orgId);
      await fetchData();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        console.log(err);
      }
    } finally {
      setLoading(false);
      dispatch(updateNotificationTrigger(Date.now()));
      onClose();
    }
  };

  const [deleteFromOrgs, setShowDeleteFromOrgs] = useState(false);
  const switchDeleteFromOrgsModal = () => {
    setShowDeleteFromOrgs(!deleteFromOrgs);
  };

  const [orgsToRemove, setOrgsToRemove] = useState<string[]>([]);
  const onOrgSelect = (id: string) => {
    const index = orgsToRemove.findIndex((d) => d === id);
    let arr = [...orgsToRemove];
    if (index !== -1) {
      arr = arr.filter((o, i) => i !== index);
    } else {
      arr.push(id);
    }
    setOrgsToRemove(arr);
  };
  const onOrgSelectClose = () => {
    setShowDeleteFromOrgs(false);
    setOrgsToRemove([]);
  };

  const submitOrgRemove = async () => {
    try {
      setLoading(true);
      for (let i = 0; i < orgsToRemove.length; i++) {
        await deleteUser(user.id, orgsToRemove[i]);
      }
      await fetchData();
      onClose();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useHotkeys('Enter', () => {
    if (isOpen && !loading) submitButtonRef?.current?.click();
  });

  const [showOrgDeleteModal, setShowOrgDeleteModal] = useState(false);
  const [orgForDelete, setOrgForDelete] = useState<string | null>(null);

  const openDeleteUserModalForOrg = (orgId: string) => {
    setOrgForDelete(orgId);
    setShowOrgDeleteModal(true);
  };

  // @ts-ignore
  const canHandleContent = [RolesLowerCase.ANALYST, RolesLowerCase.ORGANIZATION_ADMIN].includes(user?.role?.title);

  const getPrimaryOrgId = (): string | null => {
    return user?.organization_users?.[0]?.organization?.id || user?.organization?.id || (user as any)?.organizations?.[0]?.id || null;
  };

  useEffect(() => {
    if (isOpen) {
      if (user?.organization_users?.length === 1 && user.role.title !== RolesLowerCase.SUPER_ADMIN) {
        switchConfirmDeleteModal();
      }
      fetchAllUserFolders();
    }
  }, [isOpen, user]);

  return (
    <>
      <Modal open={isOpen} onClose={onClose} loading={loading}>
        <div className={styles.modalBody}>
          <div className={styles.modalTitle}>Delete ({user.email})</div>
          <div className={styles.form}>
            <div className={styles.selectedUser}>
              {user?.organization_users && user?.organization_users?.length > 0 && (
                <ul className={styles.selectedUser}>
                  <span className={styles.listHeader}>List of organisations:</span>
                  {user.organization_users?.map((u) => (
                    <li key={u.id} className={styles.orgObj}>
                      <div className={styles.orgName}>{u?.organization?.name}</div> -
                      <div className={styles.role}>{displayRole(u?.role?.title)} </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={styles.actionBtns}>
              {user?.role?.title === RolesLowerCase.SUPER_ADMIN && user.organization_users && user.organization_users?.length > 0 && (
                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  onClick={submit}
                >
                  <span>Revoke Super Admin Rights</span>
                </ContainedButton>
              )}

              {user.organization_users && user.organization_users?.length > 1 && (
                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  onClick={switchDeleteFromOrgsModal}
                >
                  <span>Select organisations to remove user from</span>
                </ContainedButton>
              )}

              <ContainedButton
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                disabled={loading}
                onClick={proceedToConfirmDeleteModal}
              >
                <span>Delete user and all User’s Content</span>
              </ContainedButton>

              <OutlinedButton
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                disabled={loading}
                kind="default"
                onClick={onClose}
              >
                <span>Cancel</span>
              </OutlinedButton>
            </div>
          </div>
        </div>
        {showOrgDeleteModal && orgForDelete && (
          <DeleteUserModal
            isOpen={showOrgDeleteModal}
            switchModal={() => {
              setShowOrgDeleteModal(false);
              setOrgForDelete(null);
            }}
            fetchData={async () => {
              await fetchData();
              setShowOrgDeleteModal(false);
              setConfirmDeletion(false);
              setShowDeleteFromOrgs(false);
              setOrgForDelete(null);
              switchModal();
            }}
            orgId={orgForDelete}
            user={user}
          />
        )}

        <Modal open={deleteFromOrgs} onClose={switchDeleteFromOrgsModal} loading={loading}>
          <div className={styles.modalBody}>
            <div className={styles.modalTitle}>Please choose the organisation you want to delete user from:</div>
            <div className={styles.form}>
              {user?.organizations &&
                user.organization_users &&
                user?.organization_users?.length > 0 &&
                user?.organizations?.length > 0 && (
                  <ul className={styles.deselectList}>
                    <span className={styles.listHeader}>List of organisations:</span>
                    {user.organization_users?.map((u) => (
                      <li key={u.id} className={styles.orgObj}>
                        <div className={styles.orgName}>{u?.organization?.name}</div> -
                        <div className={styles.role}>
                          {displayRole(u?.role?.title)}{' '}
                          <ContainedButton
                            kind="red"
                            style={{ height: 28, padding: '0 10px', marginLeft: 8 }}
                            onClick={() => openDeleteUserModalForOrg(u.organization.id)}
                          >
                            Select
                          </ContainedButton>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

              <div className={styles.actionBtns}>
                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  onClick={onOrgSelectClose}
                >
                  <span ref={submitButtonRef}>Close</span>
                </ContainedButton>
              </div>
            </div>
          </div>
        </Modal>

        <Modal open={confirmDeletion} onClose={onClose} loading={loading}>
          <div className={styles.modalBody}>
            <div className={styles.modalTitle}>Delete {user.email} from the system?</div>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                {finalFolders.length > 0 && (
                  <>
                    <div className={styles.modalTitle} style={{ fontSize: '14px' }}>
                      {user?.username || user?.email}'s folders will be deleted:
                    </div>
                    <ul className={styles.folderList} style={{ opacity: '0.7' }}>
                      {finalFolders.map((f) => (
                        <li className={styles.folder} key={f.id}>
                          <span>"{f.name}"</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {canHandleContent && user.organization_users?.length === 1 && (
                  <ContainedButton
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                    disabled={loading}
                    kind="purple"
                    onClick={() => {
                      const soleOrgId = getPrimaryOrgId();
                      if (!soleOrgId) return;
                      setConfirmDeletion(false); // close confirm
                      openDeleteUserModalForOrg(soleOrgId); // open transfer/allocate flow, even for 1 org
                    }}
                  >
                    <span>Allocate User’s Content</span>
                  </ContainedButton>
                )}

                <ContainedButton
                  kind="purple"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  onClick={switchFinalConfirm}
                >
                  <span>Delete User and all User’s Content</span>
                </ContainedButton>
                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="default"
                  onClick={onClose}
                >
                  <span>Cancel</span>
                </ContainedButton>
              </div>
            </div>
          </div>
        </Modal>

        <Modal open={finalConfirm} onClose={onClose} loading={loading}>
          <div className={styles.modalBody}>
            <div className={styles.modalTitle}>Are you sure that you want to delete {user?.username || user?.email}?</div>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                <ContainedButton
                  kind="red"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  onClick={submitDeletion}
                >
                  <span>Delete User </span>
                </ContainedButton>
                <ContainedButton
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '32px' }}
                  disabled={loading}
                  kind="purple"
                  onClick={onClose}
                >
                  <span>Cancel</span>
                </ContainedButton>
              </div>
            </div>
          </div>
        </Modal>
      </Modal>
    </>
  );
};

export default DeleteUserSuperAdminModal;
